import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore, type EntityType } from '../../stores/appStore';
import { useTodoStore } from '../../stores/todo-store';
import { mockThreads, type EmailThread } from '../email/emailTypes';
import { getProjectName } from '../todo/todoTypes';
import type { Todo } from '@signa/shared/types';
import { MOCK_NOTES, type Note, getPreview } from '../notes/notesTypes';
import { crossDomainApi, type SearchResult as CrossSearchResult } from '../../lib/cross-domain-api';

// Types

interface SearchResult {
  type: EntityType;
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  extra?: string; // e.g. project pill, snippet
  priority?: number;
}

interface ActionResult {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  action: () => void;
}

type HighlightedIndex = {
  section: number; // 0=email, 1=todo, 2=notes, 3=actions
  item: number;
};

// Fuzzy search helper

function scoreMatch(item: SearchResult, query: string): number {
  if (!query) return 1;
  const q = query.toLowerCase();
  const title = item.title.toLowerCase();
  const subtitle = item.subtitle.toLowerCase();
  if (title.includes(q)) return 3;
  if (subtitle.includes(q)) return 2;
  if (title.startsWith(q)) return 4;
  return 0;
}

function filterAndScore<T extends SearchResult>(
  items: T[],
  query: string
): T[] {
  if (!query.trim()) return items;
  return items
    .map((item) => ({ item, score: scoreMatch(item, query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

// Data builders

function buildEmailResults(threads: EmailThread[]): SearchResult[] {
  return threads.map((t) => ({
    type: 'email' as EntityType,
    id: t.id,
    title: t.messages[0]?.fromName ?? 'Unknown',
    subtitle: t.subject,
    icon: '[Email]',
    extra: t.snippet,
  }));
}

function buildTodoResults(todos: Todo[]): SearchResult[] {
  return todos.map((t) => ({
    type: 'todo' as EntityType,
    id: t.id,
    title: t.title,
    subtitle: (t.description || '').slice(0, 80),
    icon: '[Todo]',
    extra: t.projectId ? getProjectName(t.projectId) : undefined,
    priority: t.priority,
  }));
}

function buildNoteResults(notes: Note[]): SearchResult[] {
  return notes.map((n) => ({
    type: 'note' as EntityType,
    id: n.id,
    title: n.title,
    subtitle: getPreview(n.blocks),
    icon: '[Notes]',
  }));
}

// Section config

const SECTION_LABELS = ['Email', 'Todo', 'Notes', 'Actions'];
const MAX_PER_SECTION = 5;

// Component

export function CommandPalette() {
  const {
    isCommandPaletteOpen,
    setCommandPaletteOpen,
    recentlyAccessed,
    addToRecentlyAccessed,
    setActiveEmailId,
    setActiveTodoId,
    setActiveNoteId,
  } = useAppStore();

  const { todos } = useTodoStore();

  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState<HighlightedIndex>({
    section: 0,
    item: 0,
  });
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Cross-domain search state (Sprint 3.6)
  const [crossDomainResults, setCrossDomainResults] = useState<CrossSearchResult[]>([]);
  const [crossSearchLoading, setCrossSearchLoading] = useState(false);
  const [crossSearchError, setCrossSearchError] = useState(false);
  const crossSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced cross-domain search
  useEffect(() => {
    if (crossSearchDebounce.current) clearTimeout(crossSearchDebounce.current);
    const q = query.trim();
    if (q.length < 2) {
      setCrossDomainResults([]);
      setCrossSearchError(false);
      return;
    }
    setCrossSearchLoading(true);
    setCrossSearchError(false);
    crossSearchDebounce.current = setTimeout(async () => {
      try {
        const res = await crossDomainApi.search(q);
        setCrossDomainResults(res.results);
        setCrossSearchError(false);
      } catch {
        // Fall back to local results when cross-domain search fails
        setCrossDomainResults([]);
        setCrossSearchError(true);
      } finally {
        setCrossSearchLoading(false);
      }
    }, 300);
  }, [query]);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // All results
  const emailResults = useMemo(() => buildEmailResults(mockThreads), []);
  const todoResults = useMemo(() => buildTodoResults(todos), [todos]);
  const noteResults = useMemo(() => buildNoteResults(MOCK_NOTES), []);

  // Cross-domain search results mapped to SearchResult (Sprint 3.6)
  const crossEmailResults = useMemo<SearchResult[]>(() => {
    return crossDomainResults
      .filter((r) => r.type === 'email')
      .slice(0, MAX_PER_SECTION)
      .map((r) => ({
        type: 'email' as EntityType,
        id: r.id,
        title: r.title,
        subtitle: r.description ?? '',
        icon: '[Email]',
        extra: r.description ?? undefined,
      }));
  }, [crossDomainResults]);

  const crossTodoResults = useMemo<SearchResult[]>(() => {
    return crossDomainResults
      .filter((r) => r.type === 'todo')
      .slice(0, MAX_PER_SECTION)
      .map((r) => ({
        type: 'todo' as EntityType,
        id: r.id,
        title: r.title,
        subtitle: r.description ?? '',
        icon: '[Todo]',
        extra: undefined,
      }));
  }, [crossDomainResults]);

  const crossNoteResults = useMemo<SearchResult[]>(() => {
    return crossDomainResults
      .filter((r) => r.type === 'note')
      .slice(0, MAX_PER_SECTION)
      .map((r) => ({
        type: 'note' as EntityType,
        id: r.id,
        title: r.title,
        subtitle: r.description ?? '',
        icon: '[Notes]',
      }));
  }, [crossDomainResults]);

  // Use real cross-domain results when available, otherwise fall back to local search
  const hasRealResults = crossDomainResults.length > 0 && !crossSearchError;

  // Filtered results - prefer real data when available
  const filteredEmail = useMemo(
    () =>
      hasRealResults
        ? crossEmailResults
        : filterAndScore(emailResults, query).slice(0, MAX_PER_SECTION),
    [hasRealResults, crossEmailResults, emailResults, query]
  );
  const filteredTodo = useMemo(
    () =>
      hasRealResults
        ? crossTodoResults
        : filterAndScore(todoResults, query).slice(0, MAX_PER_SECTION),
    [hasRealResults, crossTodoResults, todoResults, query]
  );
  const filteredNotes = useMemo(
    () =>
      hasRealResults
        ? crossNoteResults
        : filterAndScore(noteResults, query).slice(0, MAX_PER_SECTION),
    [hasRealResults, crossNoteResults, noteResults, query]
  );

  // Build actions based on query
  const actionResults = useMemo<ActionResult[]>(() => {
    const base: ActionResult[] = [
      {
        id: 'compose',
        title: 'Compose new email',
        subtitle: 'Open the email composer',
        icon: '[Fast]',
        action: () => {
          // Trigger compose via a custom event that AppShell listens to
          window.dispatchEvent(new CustomEvent('signa:compose'));
          setCommandPaletteOpen(false);
        },
      },
      {
        id: 'new-task',
        title: 'Create new task',
        subtitle: 'Add a task to your list',
        icon: '[Fast]',
        action: () => {
          window.dispatchEvent(new CustomEvent('signa:new-task'));
          setCommandPaletteOpen(false);
        },
      },
      {
        id: 'new-note',
        title: 'Create new note',
        subtitle: 'Create a new note in the editor',
        icon: '[Fast]',
        action: () => {
          window.dispatchEvent(new CustomEvent('signa:new-note'));
          setCommandPaletteOpen(false);
        },
      },
      {
        id: 'connect-gmail',
        title: 'Connect Gmail',
        subtitle: 'Coming soon',
        icon: '[Fast]',
        action: () => {
          showToast('Coming soon');
        },
      },
      {
        id: 'connect-m365',
        title: 'Connect Microsoft 365',
        subtitle: 'Coming soon',
        icon: '[Fast]',
        action: () => {
          showToast('Coming soon');
        },
      },
      {
        id: 'open-settings',
        title: 'Open settings',
        subtitle: 'Coming soon',
        icon: '[Fast]',
        action: () => {
          showToast('Coming soon');
        },
      },
      {
        id: 'ask-ai',
        title: 'Ask AI',
        subtitle: 'Ask your data anything',
        icon: '[Fast]',
        action: () => {
          window.dispatchEvent(new CustomEvent('signa:ask-ai'));
          setCommandPaletteOpen(false);
        },
      },
    ];

    // If query looks like a todo creation ("buy milk", "call john...")
    const q = query.trim();
    if (
      q &&
      q.length > 2 &&
      !q.startsWith('/') &&
      !base.some((a) => a.title.toLowerCase().includes(q.toLowerCase()))
    ) {
      return [
        {
          id: 'create-todo',
          title: `Create task: "${q}"`,
          subtitle: 'Add this as a new todo',
          icon: '[Fast]',
          action: () => {
            window.dispatchEvent(
              new CustomEvent('signa:create-todo', { detail: { title: q } })
            );
            setCommandPaletteOpen(false);
          },
        },
        ...base,
      ];
    }

    return base;
  }, [query, setCommandPaletteOpen]);

  // Recent items from store
  const recentResults = useMemo<SearchResult[]>(() => {
    return recentlyAccessed.map((r) => {
      if (r.type === 'email') {
        const thread = mockThreads.find((t) => t.id === r.id);
        return {
          type: r.type,
          id: r.id,
          title: thread?.messages[0]?.fromName ?? r.title,
          subtitle: thread?.subject ?? '',
          icon: '[Email]',
          extra: thread?.snippet,
        };
      }
      if (r.type === 'todo') {
        const todo = todos.find((t) => t.id === r.id);
        return {
          type: r.type,
          id: r.id,
          title: todo?.title ?? r.title,
          subtitle: (todo?.description ?? '').slice(0, 80),
          icon: '[Todo]',
          extra: todo?.projectId ? getProjectName(todo.projectId) : undefined,
          priority: todo?.priority ?? 3,
        };
      }
      const note = MOCK_NOTES.find((n) => n.id === r.id);
      return {
        type: r.type,
        id: r.id,
        title: note?.title ?? r.title,
        subtitle: note ? getPreview(note.blocks) : '',
        icon: '[Notes]',
      };
    });
  }, [recentlyAccessed, todos]);

  // Active sections depending on query
  const hasQuery = query.trim().length > 0;
  const sections = hasQuery
    ? [
        { label: SECTION_LABELS[0], items: filteredEmail, type: 'search' as const },
        { label: SECTION_LABELS[1], items: filteredTodo, type: 'search' as const },
        { label: SECTION_LABELS[2], items: filteredNotes, type: 'search' as const },
        { label: SECTION_LABELS[3], items: actionResults.slice(0, MAX_PER_SECTION), type: 'action' as const },
      ]
    : [
        { label: 'Recent', items: recentResults, type: 'recent' as const },
        { label: SECTION_LABELS[3], items: actionResults.slice(0, 3), type: 'action' as const },
      ];

  // Flat list of all items with section index for keyboard nav
  const flatItems = useMemo(() => {
    const result: Array<{ section: number; item: number; data: SearchResult | ActionResult }> = [];
    sections.forEach((section, si) => {
      section.items.forEach((item, ii) => {
        result.push({ section: si, item: ii, data: item as SearchResult | ActionResult });
      });
    });
    return result;
  }, [sections]);

  // Auto-focus input on open
  useEffect(() => {
    if (isCommandPaletteOpen) {
      setQuery('');
      setHighlighted({ section: 0, item: 0 });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isCommandPaletteOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    const el = document.querySelector('[data-highlighted="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  // Navigate to item
  const navigateTo = useCallback(
    (type: EntityType, id: string, title: string) => {
      const item = { type, id, title, accessedAt: new Date() };
      addToRecentlyAccessed(item);

      if (type === 'email') {
        setActiveEmailId(id);
      } else if (type === 'todo') {
        setActiveTodoId(id);
      } else if (type === 'note') {
        setActiveNoteId(id);
      }
      setCommandPaletteOpen(false);
    },
    [addToRecentlyAccessed, setActiveEmailId, setActiveTodoId, setActiveNoteId, setCommandPaletteOpen]
  );

  // Execute highlighted item
  const executeHighlighted = useCallback(() => {
    const flat = flatItems.find(
      (f) => f.section === highlighted.section && f.item === highlighted.item
    );
    if (!flat) return;

    if (flat.data && 'action' in flat.data) {
      (flat.data as ActionResult).action();
      return;
    }
    const result = flat.data as SearchResult;
    navigateTo(result.type, result.id, result.title);
  }, [flatItems, highlighted, navigateTo]);

  // Tab cycles through sections
  const navigateSection = useCallback(
    (dir: 1 | -1) => {
      setHighlighted((prev) => {
        const sectionCount = sections.length;
        const nextSection = (prev.section + dir + sectionCount) % sectionCount;
        const sectionItems = sections[nextSection]?.items.length ?? 0;
        return {
          section: nextSection,
          item: Math.min(prev.item, Math.max(0, sectionItems - 1)),
        };
      });
    },
    [sections]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlighted((prev) => {
            const sectionItems = sections[prev.section]?.items.length ?? 0;
            if (prev.item < sectionItems - 1) {
              return { ...prev, item: prev.item + 1 };
            }
            // Wrap or go to next section
            if (prev.section < sections.length - 1) {
              const nextSection = prev.section + 1;
              return { section: nextSection, item: 0 };
            }
            return { section: 0, item: 0 };
          });
          break;

        case 'ArrowUp':
          e.preventDefault();
          setHighlighted((prev) => {
            if (prev.item > 0) {
              return { ...prev, item: prev.item - 1 };
            }
            if (prev.section > 0) {
              const prevSection = prev.section - 1;
              const prevSectionItems = sections[prevSection]?.items.length ?? 0;
              return { section: prevSection, item: Math.max(0, prevSectionItems - 1) };
            }
            return prev;
          });
          break;

        case 'Tab':
          e.preventDefault();
          navigateSection(e.shiftKey ? -1 : 1);
          break;

        case 'Enter':
          e.preventDefault();
          executeHighlighted();
          break;

        case 'Escape':
          e.preventDefault();
          setCommandPaletteOpen(false);
          break;
      }
    },
    [sections, navigateSection, executeHighlighted, setCommandPaletteOpen]
  );

  // Toast helper
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setCommandPaletteOpen(false);
    setTimeout(() => setToastMessage(null), 2500);
  };

  if (!isCommandPaletteOpen) return null;

  const isHighlighted = (section: number, item: number) =>
    highlighted.section === section && highlighted.item === item;

  return (
    <>
      <AnimatePresence>
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50"
          style={{ backgroundColor: 'var(--bg-overlay)' }}
          onClick={() => setCommandPaletteOpen(false)}
        />

        <motion.div
          key="modal"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          className="fixed left-1/2 top-1/2 z-50 flex flex-col rounded-xl"
          style={{
            width: '640px',
            maxHeight: '480px',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-strong)',
            boxShadow: 'var(--shadow-lg)',
            outline: 'none',
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div
            className="flex items-center gap-3 px-5 py-4 shrink-0"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            <svg
              className="shrink-0"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ color: 'var(--text-tertiary)' }}
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlighted({ section: 0, item: 0 });
              }}
              placeholder="Search or type a command..."
              className="flex-1 bg-transparent outline-none text-lg"
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'DM Sans, system-ui, sans-serif',
              }}
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery('')}
                className="w-6 h-6 flex items-center justify-center rounded text-xs font-mono transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--text-tertiary)';
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12" />
                  <path d="M18 6L6 18" />
                </svg>
              </button>
            )}
            <kbd
              className="px-1.5 py-0.5 text-xs font-mono rounded"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                color: 'var(--text-tertiary)',
                border: '1px solid var(--border-strong)',
              }}
            >
              esc
            </kbd>
          </div>

          {/* Results */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto"
            style={{ overscrollBehavior: 'contain' }}
          >
            {sections.map((section, si) => {
              if (section.items.length === 0) return null;
              return (
                <div key={section.label} className="py-1">
                  {/* Section header */}
                  <div
                    className="px-5 py-2 text-xs font-mono uppercase tracking-wider"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    {section.label}
                  </div>

                  {/* Section items */}
                  {section.items.map((item, ii) => {
                    const highlighted = isHighlighted(si, ii);
                    const isAction = section.type === 'action';
                    const searchItem = item as SearchResult;
                    const actionItem = item as ActionResult;

                    return (
                      <div
                        key={isAction ? actionItem.id : searchItem.id}
                        data-highlighted={highlighted}
                        className="flex items-center gap-3 px-5 py-2.5 mx-2 rounded-lg cursor-pointer transition-colors"
                        style={{
                          backgroundColor: highlighted
                            ? 'var(--bg-active)'
                            : 'transparent',
                        }}
                        onClick={() => {
                          if (isAction) {
                            actionItem.action();
                          } else {
                            navigateTo(
                              searchItem.type,
                              searchItem.id,
                              searchItem.title
                            );
                          }
                        }}
                        onMouseEnter={() =>
                          setHighlighted({ section: si, item: ii })
                        }
                      >
                        {/* Icon */}
                        <span
                          className="shrink-0 text-base"
                          style={{ width: '20px', textAlign: 'center' }}
                        >
                          {isAction ? actionItem.icon : searchItem.icon}
                        </span>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className="text-sm font-medium truncate"
                              style={{
                                color: 'var(--text-primary)',
                                fontFamily: 'DM Sans, system-ui, sans-serif',
                              }}
                            >
                              {isAction ? actionItem.title : searchItem.title}
                            </span>
                            {/* Project pill for todos */}
                            {!isAction &&
                              searchItem.type === 'todo' &&
                              searchItem.extra && (
                                <span
                                  className="shrink-0 px-1.5 py-0.5 text-xs rounded-full"
                                  style={{
                                    backgroundColor:
                                      'var(--bg-elevated)',
                                    color: 'var(--text-secondary)',
                                    fontSize: '0.65rem',
                                  }}
                                >
                                  {searchItem.extra}
                                </span>
                              )}
                            {/* Priority dot */}
                            {!isAction &&
                              searchItem.type === 'todo' &&
                              searchItem.priority && (
                                <span
                                  className="shrink-0 w-2 h-2 rounded-full"
                                  style={{
                                    backgroundColor: `var(--priority-${searchItem.priority})`,
                                  }}
                                />
                              )}
                          </div>
                          <div
                            className="text-xs truncate mt-0.5"
                            style={{
                              color: 'var(--text-secondary)',
                              fontFamily: 'DM Sans, system-ui, sans-serif',
                            }}
                          >
                            {isAction ? actionItem.subtitle : searchItem.subtitle}
                          </div>
                        </div>

                        {/* Keyboard hint */}
                        {highlighted && (
                          <span
                            className="shrink-0 text-xs font-mono uppercase tracking-wide"
                            style={{ color: 'var(--text-tertiary)' }}
                          >
                            enter
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Empty state */}
            {flatItems.length === 0 && query.trim() && (
              <div
                className="flex flex-col items-center justify-center py-12 text-center"
              >
                <span
                  className="text-xs font-mono uppercase tracking-wide mb-3 px-2 py-1 rounded-full"
                  style={{
                    backgroundColor: 'var(--bg-elevated)',
                    color: 'var(--text-tertiary)',
                    border: '1px solid var(--border)',
                  }}
                >
                  no match
                </span>
                <p
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  No results for{' '}
                  <span
                    className="font-medium"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    "{query}"
                  </span>
                </p>
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  Try searching for emails, todos, notes, or actions
                </p>
              </div>
            )}

            {/* No query + no recent */}
            {!hasQuery && recentResults.length === 0 && (
              <div
                className="flex flex-col items-center justify-center py-12 text-center"
              >
                <span
                  className="text-xs font-mono uppercase tracking-wide mb-3 px-2 py-1 rounded-full"
                  style={{
                    backgroundColor: 'var(--bg-elevated)',
                    color: 'var(--text-tertiary)',
                    border: '1px solid var(--border)',
                  }}
                >
                  CMD K
                </span>
                <p
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Start typing to search or pick an action
                </p>
              </div>
            )}
          </div>

          {/* Footer keyboard hints */}
          <div
            className="flex items-center gap-4 px-5 py-2 shrink-0"
            style={{
              borderTop: '1px solid var(--border)',
              color: 'var(--text-tertiary)',
              fontSize: '0.65rem',
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            <span>
              <kbd
                className="px-1 py-0.5 rounded text-xs"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border-strong)',
                }}
              >
                Up/Down
              </kbd>{' '}
              navigate
            </span>
            <span>
              <kbd
                className="px-1 py-0.5 rounded text-xs"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border-strong)',
                }}
              >
                Enter
              </kbd>{' '}
              select
            </span>
            <span>
              <kbd
                className="px-1 py-0.5 rounded text-xs"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border-strong)',
                }}
              >
                tab
              </kbd>{' '}
              sections
            </span>
            <span>
              <kbd
                className="px-1 py-0.5 rounded text-xs"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border-strong)',
                }}
              >
                esc
              </kbd>{' '}
              close
            </span>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            key="toast"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.15 }}
            className="fixed bottom-8 left-1/2 z-[60] px-4 py-2.5 rounded-xl"
            style={{
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--bg-elevated)',
              border: '1px solid var(--border-strong)',
              boxShadow: 'var(--shadow-lg)',
              color: 'var(--text-secondary)',
              fontFamily: 'DM Sans, system-ui, sans-serif',
              fontSize: '0.875rem',
            }}
          >
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
